import { ActionIconProps, Mark, Text, UnstyledButton } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, forwardRef } from "react";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { deleteTipoDeJogada, QUERY_KEY } from "../../firestore";

type RemoveButtonProperties = PropsWithChildren<
  ActionIconProps & { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; tipoDeJogada: TTipoDeJogada }
>;
type TDeleteResponse = Awaited<ReturnType<typeof deleteTipoDeJogada>>;

const RemoveTipoDeJogadaButton = forwardRef<HTMLButtonElement, RemoveButtonProperties>(
  ({ tipoDeJogada, children, ...properties }, reference) => {
    const queryClient = useQueryClient();
    const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);

    const { mutate: deleteProjetoMutation, isPending } = useMutation<TDeleteResponse, Error>({
      mutationFn: () => {
        if (!activeEmpresaId) {
          throw new Error("Empresa não selecionada");
        }
        return deleteTipoDeJogada(activeEmpresaId, tipoDeJogada.id);
      },
      onSuccess() {
        return queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      },
      onError(error) {
        captureException(error, true);
      }
    });

    /**
     * Handles the click event on the button.
     * @param event - The event that triggered the click.
     */
    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();

      if (properties.onClick) {
        properties.onClick(event);
      }

      modals.openConfirmModal({
        title: "Remover tipoDeJogada de inovação",
        centered: true,
        children: (
          <Text size="sm">
            Tem certeza que deseja remover a tipoDeJogada de inovação <Mark>{tipoDeJogada.nome}</Mark>
          </Text>
        ),
        labels: {
          confirm: "Sim",
          cancel: "Não"
        },
        confirmProps: { color: "red" },
        onConfirm: () => {
          void deleteProjetoMutation();
        }
      });
    }

    return (
      <UnstyledButton disabled={isPending} ref={reference} {...properties} onClick={handleClick}>
        {children}
      </UnstyledButton>
    );
  }
);

RemoveTipoDeJogadaButton.displayName = "RemoveTipoDeJogadaButton";

export default RemoveTipoDeJogadaButton;
